import Fuse from "fuse.js";
import { EMPTY_ARRAY } from "lib";
import {
  PIC_DELIMITER,
  SideLoadedMatchedField,
} from "constants/sideLoadedBusinesses";

export interface SearchFormattedSideLoadedBusiness {
  sideLoadedBusiness: SideLoadedBusiness;
  formattedName: string;
}

// Fuse options for searching side loaded businesses.
// These should be pretty good for finding a sweet spot of results.
// One common idiom it needs to handle is finding "Kevin Turner"
// when searching for any of "Turner, K", "Turner Associates", "Kevin T", etc - by tokeninzing down to
// simplified text, we should be able to get some results for all of these, without too much noise.
// ref: https://www.fusejs.io/api/options.html
export const FUSE_OPTIONS: Fuse.IFuseOptions<SearchFormattedSideLoadedBusiness> =
  {
    keys: [
      { name: "sideLoadedBusiness.name", weight: 2 },
      { name: "formattedName", weight: 3 },
      { name: "sideLoadedBusiness.abn", weight: 1 },
    ],
    includeScore: true,
    isCaseSensitive: false,
    minMatchCharLength: 2, // ignore single character matches
    shouldSort: true,
    useExtendedSearch: true, // for tokenizing the search query and joining by "|" (OR)
    findAllMatches: true, // better rank multi token matches
    threshold: 0.2,
    // for matching primarily on names, the location of where the match occurs has little
    // to do with the ranking of the match, so lets ignore it.
    // e.g.
    //   - "Kevin Turner & John Carter of Mars"
    //   - "John Carter of Mars & Kevin Turner"
    // both should be a good match for Kevin
    // https://www.fusejs.io/concepts/scoring-theory.html#distance-threshold-and-location
    ignoreLocation: true,
    // complicated by using tokenized extended search, because we break up the search query to tokens,
    // the standard Field-length Norm setting means that we would always favor the shorter indexes,
    // https://www.fusejs.io/concepts/scoring-theory.html#field-length-norm
    ignoreFieldNorm: true,
  };

// Tokenize the search name field into a format that Fuse can easily search.
// - strip punctuation
// - convert whitespace to single space
export function formatSearchNameField(name: string): string {
  return name
    .replace(/[^\w\s]/g, " ")
    .trim()
    .split(/\s+/)
    .join(" ");
}

const IGNORED_SEARCH_TOKENS = ["and", "co", "inc", "ltd", "pty", "the"];

// Tokenize the search query into a format that Fuse can understand - basically
// - strip punctuation
// - split on space
// - ignore single character tokens
// - use include match with short tokens
export function formatSearchQueryTokens(query: string): string[] {
  return query
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(n => n.length >= 2)
    .filter(n => !IGNORED_SEARCH_TOKENS.includes(n.toLowerCase()))
    .map(term => (term.length <= 3 ? `'${term}` : term));
}

export interface SideLoadedBusinessWithMatches {
  sideLoadedBusiness: SideLoadedBusiness;
  matchedFields: SideLoadedMatchedField[];
}

// PIC/ABN/email are identifiers, not names - a one-character difference is a different
// property/business, not a typo. Match these with a normalized substring check rather than
// Fuse's fuzzy (Levenshtein-style) matching, mirroring the same normalization the backend
// applies when storing these fields.
export const normalizePicOrAbn = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const normalizeDigits = (value: string): string => value.replace(/\D/g, "");

export const normalizeLower = (value: string): string =>
  value.trim().toLowerCase();

export function matchSideLoadedBusinessIdentifiers(
  businesses: SideLoadedBusiness[],
  query: string,
): SideLoadedBusinessWithMatches[] {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 2) {
    return EMPTY_ARRAY;
  }

  const picQuery = normalizePicOrAbn(trimmedQuery);
  const lowerQuery = normalizeLower(trimmedQuery);

  // Only treat the query as an ABN search when it's actually numeric - otherwise a
  // mostly-alpha query (e.g. "alai0") normalizes down to a trivial digit fragment that
  // would coincidentally substring-match almost any ABN.
  const isNumericQuery = /^\d+$/.test(trimmedQuery.replace(/\s/g, ""));
  const digitsQuery = isNumericQuery ? normalizeDigits(trimmedQuery) : "";

  return businesses.reduce(
    (matches: SideLoadedBusinessWithMatches[], business) => {
      const matchedFields: SideLoadedMatchedField[] = [];

      if (
        digitsQuery &&
        business.abn &&
        normalizeDigits(business.abn).includes(digitsQuery)
      ) {
        matchedFields.push("abn");
      }

      if (
        picQuery &&
        business.pics.split(PIC_DELIMITER).some(pic => pic.includes(picQuery))
      ) {
        matchedFields.push("pic");
      }

      if (
        business.brands &&
        normalizeLower(business.brands).includes(lowerQuery)
      ) {
        matchedFields.push("brands");
      }

      if (
        business.email &&
        normalizeLower(business.email).includes(lowerQuery)
      ) {
        matchedFields.push("email");
      }

      if (matchedFields.length) {
        matches.push({ sideLoadedBusiness: business, matchedFields });
      }
      return matches;
    },
    [],
  );
}
